import * as React from 'react';

import { useMoon } from '@pricemoov-oss/moon';

import useProductCatalogMetadataQuery, {
    ProductCatalogMetadata,
    updateProductCatalogMetadata,
} from 'redesign/apiREST/queries/useProductCatalogMetadataQuery';
import { useAuthManager } from 'redesign/components/common/contexts/AuthManager';

export interface ProductCatalogMetadataContextProps {
    productCatalogMetadata?: ProductCatalogMetadata;
    isLoading?: boolean;
    isFetching?: boolean;
    hasError?: boolean;
    isEditable?: boolean;
    updateMetadata?: (newMetadata: Partial<ProductCatalogMetadata>) => void;
    refetch?: () => void;
}

const ProductCatalogMetadataContext = React.createContext<ProductCatalogMetadataContextProps>({});

function ProductCatalogMetadataManager({ children }): JSX.Element {
    const { store } = useMoon();
    const { isAuthenticated } = useAuthManager();

    const [{ data: productCatalogMetadata, isLoading, isFetching, error }, { refetch }] =
        useProductCatalogMetadataQuery({ enabled: !!isAuthenticated() });

    const isEditable = ['failed', 'not-ready'].includes(productCatalogMetadata?.status);

    const updateMetadata = React.useCallback(
        (newMetadata: Partial<ProductCatalogMetadata>) => {
            updateProductCatalogMetadata(store, {
                ...productCatalogMetadata,
                ...newMetadata,
            });
        },
        [store, productCatalogMetadata]
    );

    return (
        <ProductCatalogMetadataContext.Provider
            value={{
                productCatalogMetadata,
                isLoading,
                isFetching,
                hasError: !!error,
                isEditable,
                updateMetadata,
                refetch,
            }}
        >
            {children}
        </ProductCatalogMetadataContext.Provider>
    );
}

export function useProductCatalogMetadataManager(): ProductCatalogMetadataContextProps {
    return React.useContext(ProductCatalogMetadataContext);
}

export default ProductCatalogMetadataManager;
